// frontend/src/components/DataErasureSection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, AlertTriangle, Clock, XCircle, Loader2, X, ShieldAlert } from 'lucide-react';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore';

/**
 * Sección de Eliminación de Datos — visible solo para idRole=2 (Admin Cliente).
 * Se monta dentro de ClientPage.jsx, tab "Plan".
 *
 * Estados posibles:
 *  - Sin solicitud activa → botón "Solicitar eliminación"
 *  - pending → mensaje "Revise su email"
 *  - confirmed → mensaje con fecha + botón "Cancelar"
 */
export const DataErasureSection = ({ userRole }) => {
  const { t } = useLanguageStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [erasureRequest, setErasureRequest] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/data-erasure/status');
      setErasureRequest(res.data.request);
    } catch {
      setErasureRequest(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Solo visible para Admin del cliente (idRole=2)
  // ⚠️ DEBE ir DESPUÉS de todos los hooks (regla de React)
  if (Number(userRole) !== 2) return null;

  // ESC para cerrar modal
  useEffect(() => {
    if (!showConfirmModal) return;
    const h = (e) => { if (e.key === 'Escape') setShowConfirmModal(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showConfirmModal]);

  const handleRequest = async () => {
    try {
      setSubmitting(true);
      setFeedback({ type: '', message: '' });
      await apiClient.post('/data-erasure/request', { reason: reason || null });
      setShowConfirmModal(false);
      setReason('');
      setFeedback({
        type: 'success',
        message: t('data_erasure.pending_message') || 'Solicitud enviada. Revise su email para confirmar.',
      });
      await fetchStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFeedback({
        type: 'error',
        message: typeof detail === 'string' ? detail : (t('errors.ERR_GENERAL') || 'Error al procesar la solicitud.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    try {
      setSubmitting(true);
      setFeedback({ type: '', message: '' });
      await apiClient.post('/data-erasure/cancel');
      setFeedback({
        type: 'success',
        message: t('data_erasure.cancel_success') || 'Solicitud cancelada exitosamente.',
      });
      await fetchStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFeedback({
        type: 'error',
        message: typeof detail === 'string' ? detail : (t('errors.ERR_GENERAL') || 'Error al procesar la solicitud.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 py-6 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs font-medium">{t('common.loading') || 'Cargando...'}</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="mt-6 border border-red-200 rounded-2xl bg-red-50/30 p-5 sm:p-6">
        <h3 className="text-sm font-black text-red-700 flex items-center gap-2 mb-4">
          <ShieldAlert className="w-4 h-4" />
          {t('data_erasure.request_title') || 'Eliminación de Datos'}
        </h3>

        {/* Feedback */}
        {feedback.message && (
          <div
            className={`mb-4 p-3 rounded-xl text-xs font-bold ${feedback.type === 'error'
              ? 'bg-red-100 text-red-700 border border-red-200'
              : 'bg-green-100 text-green-700 border border-green-200'
              }`}
          >
            {feedback.message}
          </div>
        )}

        {/* Estado: sin solicitud activa */}
        {!erasureRequest && (
          <div>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              {t('data_erasure.request_warning') || 'Al solicitar la eliminación, todos sus datos serán borrados permanentemente después de un período de gracia de 30 días. Esta acción no se puede deshacer.'}
            </p>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('data_erasure.request_button') || 'Solicitar eliminación de mis datos'}
            </button>
          </div>
        )}

        {/* Estado: pending */}
        {erasureRequest?.status === 'pending' && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 leading-relaxed">
              {t('data_erasure.pending_message') || 'Solicitud enviada. Revise su email para confirmar la eliminación.'}
            </p>
          </div>
        )}

        {/* Estado: confirmed */}
        {erasureRequest?.status === 'confirmed' && (
          <div>
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <p className="font-bold mb-1">
                  {t('data_erasure.confirmed_message') || 'Eliminación programada. Sus datos serán eliminados permanentemente en la fecha indicada.'}
                </p>
                <p>
                  {t('data_erasure.days_remaining') || 'Días restantes'}: {' '}
                  <strong>{erasureRequest.daysRemaining}</strong>
                </p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-600 text-white rounded-xl text-xs font-bold hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <XCircle className="w-3.5 h-3.5" />
              )}
              {t('data_erasure.cancel_button') || 'Cancelar solicitud de eliminación'}
            </button>
          </div>
        )}
      </div>

      {/* ── Modal de confirmación (inline, estilo ClientPage) ────────── */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
          onClick={() => { setShowConfirmModal(false); setReason(''); }}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <h3 className="text-base font-black text-slate-800">
                  {t('data_erasure.request_confirm_modal_title') || '¿Está seguro?'}
                </h3>
              </div>
              <button
                onClick={() => { setShowConfirmModal(false); setReason(''); }}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 leading-relaxed">
                  {t('data_erasure.request_confirm_modal') || 'Esta acción eliminará permanentemente todos sus datos, pipelines, proyectos e historial de DatiaFlow. No se puede deshacer.'}
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">
                  {t('data_erasure.reason_label') || 'Motivo (opcional)'}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('data_erasure.reason_placeholder') || '¿Por qué desea eliminar sus datos?'}
                  maxLength={500}
                  rows={3}
                  className="w-full bg-slate-50 border-2 border-transparent rounded-xl py-2.5 px-4 outline-none font-medium text-sm transition-all focus:border-red-100 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowConfirmModal(false); setReason(''); }}
                className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors"
              >
                {t('common.buttons.cancel') || 'Cancelar'}
              </button>
              <button
                type="button"
                onClick={handleRequest}
                disabled={submitting}
                className="flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-red-700 transition-all disabled:bg-slate-300"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {t('data_erasure.request_button') || 'Solicitar eliminación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
